/**
 * WithingsApp - Router-basierte Version
 * Layout und Navigation für Withings Scale Integration mit React Router
 */

import { Scale, TrendingUp, Activity, RefreshCw, BarChart3 } from "lucide-react";
import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";

import { TokenStatusIndicator } from "./components/atoms/TokenStatusIndicator";
import { WithingsSyncIconButton } from "./components/ui";
import { useWithingsAuth } from "./hooks/useWithingsAuth";
import { useWithingsData } from "./hooks/useWithingsData";

/**
 * WHAT:
 *   Main application component for Withings scale integration with router-based navigation.
 *
 * WHY:
 *   Provides layout and navigation for Withings health data management with tab-based interface.
 *
 * PROPS:
 *   None (root component using React Router)
 *
 * BEHAVIOR:
 *   - Manages authentication state and token status
 *   - Provides tab navigation (Overview, Body Composition, Trends)
 *   - Displays sync button and status indicators
 *   - Handles data loading and error states
 *   - Integrates with React Router for navigation
 *
 * RISKS:
 *   - NOTE: Authentication state management is complex - may have sync issues
 *   - NOTE: Data loading depends on authentication - may fail if token expires
 *   - WARN: Router navigation may cause data loss if not saved before navigation
 *
 * @returns {JSX.Element} Withings application component with router-based navigation
 *
 */
export const WithingsApp: React.FC = () => {
  const { authenticate, disconnect, error, isAuthenticated, isAuthenticating, isInitialized } =
    useWithingsAuth();

  const { onSyncComplete, syncStatus } = useWithingsData();

  const location = useLocation();
  const navigate = useNavigate();

  // Tab configuration
  const tabs = [
    {
      id: "overview",
      label: "Overview",
      path: "/nexus/health/withings/overview",
    },
    {
      id: "body-composition",
      label: "Body Composition",
      path: "/nexus/health/withings/body-composition",
    },
    { id: "trends", label: "Trends", path: "/nexus/health/withings/trends" },
    { id: "sync", label: "Sync", path: "/nexus/health/withings/sync" },
    { id: "tokens", label: "Tokens", path: "/nexus/health/withings/tokens" },
  ];

  const currentTab = tabs.find((tab) => location.pathname === tab.path) ?? tabs[0];

  const handleTabClick = (path: string) => {
    // Reason: navigate from react-router-dom v6 returns void, not a Promise, but TypeScript may infer Promise type

    navigate(path);
  };

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
          <p className="text-slate-300">Initializing Withings Integration...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-6">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-slate-800/50 border-slate-700 text-white">
            <div className="p-8 text-center">
              <div className="mb-6">
                <Scale className="h-16 w-16 text-blue-500 mx-auto mb-4" />
                <h1 className="text-3xl font-bold text-white mb-2">Withings Scale Integration</h1>
                <p className="text-slate-300">
                  Connect your Withings scale for detailed body analysis
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="text-center p-4 bg-slate-700/50 rounded-lg">
                  <TrendingUp className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-white">Smart Sync</h3>
                  <p className="text-sm text-slate-400">
                    Automatic synchronization from 01.01.2025
                  </p>
                </div>
                <div className="text-center p-4 bg-slate-700/50 rounded-lg">
                  <BarChart3 className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-white">9 Measurements</h3>
                  <p className="text-sm text-slate-400">Weight, body fat, muscle mass & more</p>
                </div>
                <div className="text-center p-4 bg-slate-700/50 rounded-lg">
                  <Activity className="h-8 w-8 text-purple-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-white">Trends & Charts</h3>
                  <p className="text-sm text-slate-400">Beautiful visualizations of your data</p>
                </div>
              </div>

              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold"
                disabled={isAuthenticating}
                onClick={() => {
                  void authenticate();
                }}
              >
                {isAuthenticating ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Scale className="h-4 w-4 mr-2" />
                    Connect Withings Account
                  </>
                )}
              </Button>

              {error ? (
                <div className="mt-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300">
                  {error}
                </div>
              ) : null}
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="w-full max-w-none px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <img
                  alt="Withings"
                  className="h-10 w-10 object-contain"
                  src="/src/assets/withings-logo.png"
                />
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-slate-800" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Withings Scale</h1>
                <div className="flex items-center space-x-4 text-sm text-slate-400">
                  <span className="flex items-center space-x-1">
                    <span className="w-2 h-2 bg-blue-500 rounded-full" />
                    <span>{syncStatus.totalMeasurements} Measurements</span>
                  </span>
                  <span>•</span>
                  <span className="flex items-center space-x-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full" />
                    <span>
                      {syncStatus.lastSyncDate
                        ? `Last Sync: ${new Date(syncStatus.lastSyncDate).toLocaleDateString("en-US")}`
                        : "Not synchronized yet"}
                    </span>
                  </span>
                  {syncStatus.daysSinceLastSync !== null && syncStatus.daysSinceLastSync > 0 && (
                    <>
                      <span>•</span>
                      <span className="flex items-center space-x-1">
                        <span className="w-2 h-2 bg-yellow-500 rounded-full" />
                        <span>{syncStatus.daysSinceLastSync} days since last sync</span>
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* Token Status Indicator */}
              <div className="mr-4">
                <TokenStatusIndicator
                  className="token-status-withings"
                  showDetails={false}
                  size="sm"
                />
              </div>

              <Button
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                onClick={() => {
                  void disconnect();
                }}
                variant="outline"
              >
                Disconnect
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="w-full max-w-none px-6">
        <nav className="border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                    currentTab?.id === tab.id
                      ? "border-blue-500 text-blue-400"
                      : "border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-600"
                  }`}
                  key={tab.id}
                  onClick={() => {
                    handleTabClick(tab.path);
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <WithingsSyncIconButton
              className="ml-2"
              onSyncComplete={() => {
                void onSyncComplete();
              }}
            />
          </div>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="w-full max-w-none px-6 py-6">
        <div className="space-y-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
};
